import React from 'react';
import {
  Box,
  VStack,
  Flex,
  Text,
  Badge,
  Icon,
  Spinner,
  IconButton,
  Heading,
  Divider,
  useColorModeValue
} from '@chakra-ui/react';
import { FaPhone, FaHistory, FaRedo } from 'react-icons/fa';
import moment from 'moment';

/**
 * Component to display call history for a lead
 */
const NewKBCallHistory = ({ callHistory, isLoading, onRefresh }) => {
  // Color mode values
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const textColor = useColorModeValue('gray.800', 'white');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const itemBgColor = useColorModeValue('white', 'gray.700');
  const itemBorderColor = useColorModeValue('gray.100', 'gray.600');
  
  // Helper function to get color scheme based on call outcome
  const getOutcomeColor = (outcome) => {
    switch (outcome) {
      case 'answered': return 'green';
      case 'not_answered': return 'red';
      case 'rescheduled': return 'blue';
      default: return 'gray';
    }
  };
  
  // Helper function to get formatted outcome text
  const getOutcomeText = (outcome) => {
    switch (outcome) {
      case 'answered': return 'Answered';
      case 'not_answered': return 'Not Answered';
      case 'rescheduled': return 'Rescheduled';
      default: return 'Unknown';
    }
  };

  return (
    <Box 
      p={4} 
      borderWidth="1px" 
      borderRadius="md" 
      bg={bgColor}
      h="300px"
      overflowY="auto"
    >
      <Flex justify="space-between" align="center" mb={3}>
        <Heading size="sm" display="flex" alignItems="center">
          <Icon as={FaHistory} mr={2} />
          Call History
        </Heading>
        <IconButton
          icon={<FaRedo />}
          size="sm"
          variant="ghost"
          aria-label="Refresh call history"
          onClick={onRefresh}
          isDisabled={isLoading}
        />
      </Flex>
      
      <Divider mb={3} />
      
      {isLoading ? (
        <Flex justify="center" align="center" h="200px">
          <Spinner size="md" />
        </Flex>
      ) : callHistory.length === 0 ? (
        <Flex 
          justify="center" 
          align="center" 
          h="200px"
          color={mutedColor}
          flexDirection="column"
        >
          <Icon as={FaPhone} fontSize="2xl" mb={2} />
          <Text>No call history found</Text>
        </Flex>
      ) : (
        <VStack spacing={3} align="stretch">
          {callHistory.map((call, index) => (
            <Box
              key={call._id || index}
              p={3}
              borderWidth="1px"
              borderRadius="md"
              borderColor={itemBorderColor}
              bg={itemBgColor}
            >
              <Flex justify="space-between" mb={1}>
                <Badge colorScheme={getOutcomeColor(call.outcome)}>
                  {getOutcomeText(call.outcome)}
                </Badge>
                <Text fontSize="xs" color={mutedColor}>
                  {moment(call.timestamp).format('MMM D, YYYY [at] h:mm A')}
                </Text>
              </Flex>
              
              {call.notes && (
                <Box fontSize="sm" mt={1} color={textColor}>
                  {call.notes.split('\n').map((line, i) => (
                    <React.Fragment key={i}>
                      {line}
                      {i < call.notes.split('\n').length - 1 && <br />}
                    </React.Fragment>
                  ))}
                </Box>
              )}
              
              {call.rescheduledTo && (
                <Text fontSize="xs" color={mutedColor} mt={1}>
                  {call.outcome === 'rescheduled' ? 'Rescheduled for: ' : 'Follow-up: '}
                  {moment(call.rescheduledTo).format('MMM D, YYYY [at] h:mm A')}
                </Text>
              )}
            </Box>
          ))}
        </VStack>
      )}
    </Box>
  );
};

export default NewKBCallHistory;
